
import { defineComponent, onMounted, watch, ref } from 'vue'
import { Loader } from '@googlemaps/js-api-loader'
import Logger from '@/services/logger'
import { DEFAULT_CENTER_LAT, DEFAULT_CENTER_LNG, DEFAULT_ZOOM } from '@/constants/map'

/**
 * 文字列の緯度をnumberに変換する
 * 空文字の場合、nullにする
 */
const latToNum = (lat: string | null) => {
  return lat != null && lat !== '' ? Number(lat) : null
}
/**
 * 文字列の経度をnumberに変換する
 * 空文字の場合、nullにする
 */
const lngToNum = (lng: string | null) => {
  return lng != null && lng !== '' ? Number(lng) : null
}

/**
 * Mapを表示するコンポーネント
 */
export default defineComponent({
  name: 'JrMap',
  emits: ['update:lat', 'update:lng', 'update:centerLat', 'update:centerLng'],
  props: {
    // 中心位置
    centerLat: {
      type: String,
      required: true,
    },
    // 中心位置
    centerLng: {
      type: String,
      required: true,
    },
    // ピン位置
    lat: {
      type: String,
      required: true,
    },
    // ピン位置
    lng: {
      type: String,
      required: true,
    },
    // 読み込むかどうか falseからtrueにした地点で読み込まれる
    load: {
      type: Boolean,
      required: false,
      default: true,
    },
    isEdit: {
      type: Boolean,
      required: false,
      default: true,
    },
    zoom: {
      type: Number,
      required: false,
      default: DEFAULT_ZOOM,
    },
  },
  setup(props, context) {
    const apiKey = process.env.VUE_APP_GOOGLE_API_KEY
    if (apiKey == null) {
      // APIkeyが存在しない場合、エラーとする
      throw new Error('Google API key not setting.')
    }
    const loaded = ref<boolean>(false)

    // input要素のID
    const mapElement = ref<HTMLElement>()

    onMounted(async () => {
      // マウント後、loadをwatchしtrueなら読み込む
      watch(
        () => props.load,
        async (newLoad) => {
          if (!newLoad || loaded.value) {
            // falseか読み込み済みなら何もしない
            return
          }
          Logger.info('Google Map API load.')
          // googleAPIを読み込む
          const loader = new Loader({
            apiKey: apiKey,
            version: 'weekly',
            channel: '2',
          })
          const googleApi = await loader.load()

          // mapのエレメントを取得
          if (mapElement.value == null) {
            // 存在しない場合は例外
            throw new Error('map element not found.')
          }

          // mapの設定
          const mapOptions = {
            center: {
              // ピンがあればそれが中心、無ければcenter、それも無ければデフォルト位置
              lat: latToNum(props.lat) ?? latToNum(props.centerLat) ?? Number(DEFAULT_CENTER_LAT),
              lng: lngToNum(props.lng) ?? lngToNum(props.centerLng) ?? Number(DEFAULT_CENTER_LNG),
            },
            zoom: props.zoom,
            styles: [
              {
                featureType: 'transit.line',
                elementType: 'geometry.fill',
                stylers: [
                  {
                    visibility: 'off',
                  },
                ],
              },
              {
                featureType: 'transit.line',
                elementType: 'geometry.stroke',
                stylers: [
                  {
                    visibility: 'off',
                  },
                ],
              },
            ],
            mapTypeId: googleApi.maps.MapTypeId.HYBRID,
            tilt: 0,
            fullscreenControl: false,
            mapTypeControl: false,
            rotateControl: false,
            gestureHandling: 'greedy',
            streetViewControl: false,
          }

          // MAPを生成
          const googleMap = new googleApi.maps.Map(mapElement.value, mapOptions)

          // MAPクリック時のイベントを登録
          googleApi.maps.event.addListener(
            googleMap,
            'click',
            (event: { latLng: { lat: () => number; lng: () => number } | null }) => {
              if (props.isEdit && event.latLng != null) {
                // クリックした座標をemit
                context.emit('update:lat', event.latLng.lat().toPrecision(9))
                context.emit('update:lng', event.latLng.lng().toPrecision(9))
              }
            }
          )
          // 中心が移動終了した時のイベントを登録
          googleApi.maps.event.addListener(googleMap, 'dragend', () => {
            const latLng = googleMap.getCenter()
            if (latLng != null) {
              // MAP中心をemit
              context.emit('update:centerLat', latLng.lat().toPrecision(9))
              context.emit('update:centerLng', latLng.lng().toPrecision(9))
            }
          })

          // マーカー保持変数
          let maker: InstanceType<typeof googleApi.maps.Marker> | null = null

          // マーカー指定があれば立てる
          if (latToNum(props.lat) != null && lngToNum(props.lng) != null) {
            maker = new googleApi.maps.Marker({
              position: {
                lat: latToNum(props.lat) as number,
                lng: lngToNum(props.lng) as number,
              },
              map: googleMap,
            })
          }

          // 緯度経度が変更された際にピン立て・センタリングを行う
          watch(
            () => [props.lat, props.lng, props.centerLat, props.centerLng],
            () => {
              const center = {
                lat: latToNum(props.centerLat) ?? Number(DEFAULT_CENTER_LAT),
                lng: lngToNum(props.centerLng) ?? Number(DEFAULT_CENTER_LNG),
              }
              googleMap.setCenter(center)

              // 既にあるマーカーの削除
              if (maker != null) {
                maker.setMap(null)
              }
              // マーカー追加
              if (latToNum(props.lat) != null && lngToNum(props.lng) != null) {
                maker = new googleApi.maps.Marker({
                  position: {
                    lat: latToNum(props.lat) as number,
                    lng: lngToNum(props.lng) as number,
                  },
                  map: googleMap,
                })
              }
            },
            {
              deep: true,
            }
          )

          watch(
            () => props.zoom,
            () => {
              googleMap.setZoom(props.zoom)
            }
          )

          loaded.value = true
        },
        { immediate: true }
      )
    })

    return {
      loaded,
      mapElement,
    }
  },
})
